import { createTheme, ThemeProvider } from "@mui/material/styles";
import juice from "juice";

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#90caf9", // Light blue for contrast
    },
    secondary: {
      main: "#f48fb1", // Soft pink for secondary elements
    },
    background: {
      default: "#121212", // Dark background for the entire app
      paper: "#1e1e1e", // Slightly lighter for cards and dialogs
    },
    text: {
      primary: "#ffffff", // White text for readability
      secondary: "#b0b0b0", // Slightly gray for less emphasis
    },
  },
  components: {
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#333", // Dark menu background
          color: "#fff", // White text
          textAlign: "center",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "#fff", // White text by default
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        containedPrimary: {
          backgroundColor: "#90caf9",
          color: "#000",
          "&:hover": {
            backgroundColor: "#64b5f6",
          },
        },
        containedSecondary: {
          backgroundColor: "#f48fb1",
          "&:hover": {
            backgroundColor: "#c2185b",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e1e1e",
          color: "#fff",
          boxShadow: "none",
          borderBottom: "1px solid #333",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e1e1e",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#222",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            backgroundColor: "#333",
            color: "#fff",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#666",
            },
            "&:hover fieldset": {
              borderColor: "#999",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#90caf9",
            },
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: "#222",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#333",
          fontWeight: "bold",
          color: "#fff",
        },
        body: {
          color: "#fff",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "#1e1e1e",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: "#333",
          color: "#fff",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#fff",
          color: "#000",
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: "#ececec", // Light gray for icons in dark mode
        },
      },
    },
  },
});

export const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1976d2", // Default primary color (blue)
    },
    secondary: {
      main: "#f50057", // Pink secondary color
    },
    background: {
      default: "#f5f5f5", // Light gray background
      paper: "#fff", // White for paper elements
    },
    text: {
      primary: "#000", // Dark text for contrast
      secondary: "#555", // Slightly lighter for secondary text
    },
  },
  components: {
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#fff",
          color: "#000",
          textAlign: "center",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "#000",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none", // Keep button text normal case
        },
        containedPrimary: {
          backgroundColor: "#1976d2",
          "&:hover": {
            backgroundColor: "#115293",
          },
        },
        containedSecondary: {
          backgroundColor: "#f50057",
          "&:hover": {
            backgroundColor: "#ab003c",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff",
          color: "#000",
          boxShadow: "none",
          borderBottom: "1px solid #ddd",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {},
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            backgroundColor: "#fff",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#ddd",
            },
            "&:hover fieldset": {
              borderColor: "#aaa",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#1976d2",
            },
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#e0e0e0",
          fontWeight: "bold",
        },
        body: {
          color: "#000",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#000",
          color: "#fff",
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: "#333", // Dark gray for icons in light mode
        },
      },
    },
  },
});

export const getResourceType = (file) => {
  if (!file?.fileType) return "other";

  if (file.fileType.startsWith("image/")) return "image";
  if (file.fileType === "application/pdf") return "pdf";
  if (
    file.fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    file.fileType === "application/msword"
  )
    return "docx";
  if (
    file.fileType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    file.fileType === "application/vnd.ms-excel"
  )
    return "xlsx";
  return "other";
};

export const getResourceIcon = (type) => {
  switch (type) {
    case "image":
      return "🖼️";
    case "pdf":
      return "📄";
    case "docx":
      return "📝";
    case "xlsx":
      return "📊";
    default:
      return "📎";
  }
};

export const getPreviewType = (file) => {
  const mime = file.fileType || "";

  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";

  if (
    mime.includes("officedocument") ||
    mime.includes("msword") ||
    mime.includes("spreadsheet") ||
    mime.includes("presentation")
  ) {
    return "office";
  }

  return "unsupported";
};

export const extractStylesFromHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const styleTag = doc.querySelector("style");
  return styleTag ? styleTag.outerHTML : "";
};

export const buildFullHtml = (bodyHtml, originalHtml) => {
  const originalStyles = extractStylesFromHtml(originalHtml);

  const fullHtml = `
<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="UTF-8" />
  <title>Documento</title>
  ${originalStyles}  
</head>
<body>
  ${bodyHtml}
</body>
</html>
`;

  return fullHtml;
};

// Removed: import type { Resource } from "./types"

export function getFileTypeLabel(fileType) {
  const map = {
    "application/pdf": "PDF",
    "application/msword": "Word",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "Word",
    "application/vnd.ms-powerpoint": "PowerPoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "PowerPoint",
    "image/png": "Image",
    "image/jpeg": "Image",
    "image/gif": "Image",
    "text/plain": "Text",
  };

  return map[fileType] ?? fileType;
}

export function getFileTypeIcon(fileType) {
  if (fileType === "application/pdf") return "pdf";
  if (fileType.startsWith("image/")) return "image";
  if (fileType.includes("word") || fileType.includes("msword")) return "word";
  if (fileType.includes("powerpoint") || fileType.includes("presentation"))
    return "ppt";
  if (fileType === "text/plain") return "text";
  return "file";
}

export function formatFileSize(bytes) {
  const size = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;

  if (isNaN(size)) return "Unknown";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function getVisibilityColor(scope) {
  switch (scope) {
    case "public":
      return "default";
    case "private":
      return "secondary";
    case "department":
      return "outline";
    default:
      return "secondary";
  }
}

export const FILE_TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "application/pdf", label: "PDF" },
  { value: "application/msword", label: "Word (DOC)" },
  {
    value:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    label: "Word (DOCX)",
  },
  { value: "application/vnd.ms-powerpoint", label: "PowerPoint (PPT)" },
  {
    value:
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    label: "PowerPoint (PPTX)",
  },
  { value: "image/png", label: "PNG Image" },
  { value: "image/jpeg", label: "JPEG Image" },
  { value: "image/gif", label: "GIF Image" },
  { value: "text/plain", label: "Plain Text" },
];
